import { atom } from "jotai"
import { focusAtom } from "jotai-optics"
import { ExpandablePanel, HomePageSection, stateAtom, ToolPanel } from "@/store/undoable-state"
import { openFocusView, photosViewAtom } from "@/store/photo/photos-view-state"
import { store } from "./store"
import {
	maskOverlaySettingsAtom,
	mappedMasksAtom,
	selectedMaskGroupAtom,
	showingFloatingMaskingPanelAtom,
} from "@/store/editor/masks-state"
import { isCullingModeAtom } from "./project/projects-state"

export const viewportCanvasSettingsAtom = atom({
	isZoomableWithMouse: true,
	isDraggableWithMouse: true,
	isZoomableWithSpace: true,
	isZoomableWithZ: true,
	isZoomableWithTouchpadOnly: false, // touch pad or ctrl + mouse wheel
})

const activeToolPanelBaseAtom = focusAtom(stateAtom, (optic) =>
	optic.prop("activeEditingToolPanel"),
)

const leftExpandedPanelBaseAtom = focusAtom(stateAtom, (optic) => optic.prop("leftExpandedPanel"))

export const activeEditingToolPanelAtom = atom(
	(get) => get(activeToolPanelBaseAtom),
	(get, set, toolPanel: ToolPanel) => {
		const previousToolPanel = get(activeToolPanelBaseAtom)
		if (
			previousToolPanel !== toolPanel &&
			get(leftExpandedPanelBaseAtom) === "colormatch_samples"
		) {
			set(leftExpandedPanelBaseAtom, null)
		}

		set(activeToolPanelBaseAtom, toolPanel)
		if (toolPanel === "masking" && previousToolPanel !== "masking") {
			set(maskOverlaySettingsAtom, (settings) => ({ ...settings, showOverlay: true }))
		}

		//Crop tool can only be shown in focus view
		if (toolPanel === "crop") {
			openFocusView()
		}

		const maskGroup = get(selectedMaskGroupAtom)
		const masks = get(mappedMasksAtom)

		if (toolPanel === "masking" && !maskGroup) {
			// Make sure the most recent mask is also selected
			set(selectedMaskGroupAtom, masks.at(-1)?.properties)
			set(showingFloatingMaskingPanelAtom, true)
		}
	},
)

export const leftExpandedPanelAtom = atom(
	(get) => get(leftExpandedPanelBaseAtom),
	(_, set, panel: ExpandablePanel | null) => {
		set(leftExpandedPanelBaseAtom, panel)
	},
)

export const showingCropPanelAtom = atom(
	(get) => {
		return get(activeEditingToolPanelAtom) === "crop"
	},
	(_, set, showing: boolean) => {
		set(activeEditingToolPanelAtom, showing ? "crop" : "edit")
	},
)

export const showingMaskingPanelAtom = atom(
	(get) => {
		return get(activeEditingToolPanelAtom) === "masking"
	},
	(_, set, showing: boolean) => {
		set(activeEditingToolPanelAtom, showing ? "masking" : "edit")
		set(showingFloatingMaskingPanelAtom, showing)
	},
)

export const showingHealingPanelAtom = atom(
	(get) => {
		return get(activeEditingToolPanelAtom) === "healing"
	},
	(_, set, showing: boolean) => {
		set(activeEditingToolPanelAtom, showing ? "healing" : "edit")
	},
)

export const showingPeoplePanelAtom = atom(
	(get) => {
		return get(activeEditingToolPanelAtom) === "people"
	},
	(_, set, showing: boolean) => {
		set(activeEditingToolPanelAtom, showing ? "people" : "edit")
	},
)

export const mouseOverSelectedHealingPanelAtom = atom(false)
export const healingParametersAdjustingAtom = atom(false)
export const showAdjustingBrushCursorAtom = atom(false)

export const canShowSidePanelsAtom = atom((get) => {
	const isCulling = get(isCullingModeAtom)
	const photosView = get(photosViewAtom)

	return isCulling || (!isCulling && photosView !== "grid")
})

export const shouldShowLeftPanelAtom = atom((get) => {
	return get(showingLeftPanelAtom) && get(canShowSidePanelsAtom)
})

export const toggleLeftPanel = () => {
	const showingLeftPanel = store.get(showingLeftPanelAtom)
	store.set(showingLeftPanelAtom, !showingLeftPanel)
}

export const showingLeftPanelAtom = focusAtom(stateAtom, (optic) => optic.prop("showingLeftPanel"))

export const shouldShowRightPanelAtom = atom((get) => {
	return get(showingRightPanelAtom) && get(canShowSidePanelsAtom)
})

export const toggleRightPanel = () => {
	const showingRightPanel = store.get(showingRightPanelAtom)
	store.set(showingRightPanelAtom, !showingRightPanel)
}

export const showingRightPanelAtom = focusAtom(stateAtom, (optic) =>
	optic.prop("showingRightPanel"),
)

export const showingFilmstripBaseAtom = focusAtom(stateAtom, (optic) =>
	optic.prop("showingFilmstrip"),
)

export const showingFilmstripAtom = atom(
	(get) => {
		const previewState = get(photosViewAtom)

		if (previewState === "grid") {
			return false
		}

		return get(showingFilmstripBaseAtom)
	},
	(_, set, showing: boolean) => {
		set(showingFilmstripBaseAtom, showing)
	},
)

export const showingFilmstripExpanderAtom = atom(false)
export const filmstripExpandedAtom = atom(true)

export const showingSplashAtom = atom(true)

export const projectClosingAtom = atom(false)

export const homeSectionAtom = atom<HomePageSection>("projects")

export const isFullscreenAtom = focusAtom(stateAtom, (optic) => optic.prop("isFullscreen"))

export const isPhotoFullscreenAtom = atom(false)

export function togglePhotoFullScreen() {
	store.set(isPhotoFullscreenAtom, !store.get(isPhotoFullscreenAtom))
}

export function exitPhotoFullscreen() {
	store.set(isPhotoFullscreenAtom, false)
}

// true: the rating bar can be visible/invisible
// false: the rating bar should be hidden unconditionally.
export const showingRatingBarAtom = atom(true)

// true: rating bar is visible; ignore mouse/timer logic that would normally hide it
// false: the visibility is determined by mouse/timer logic
export const isRatingBarActiveAtom = atom(true)

export function toggleFullScreen() {
	const isFullscreen = store.get(isFullscreenAtom)
	if (!isFullscreen && !document.fullscreenElement) {
		enterFullscreen()
	} else if (isFullscreen) {
		exitFullscreen()
	}
}

export function enterFullscreen() {
	document.documentElement.requestFullscreen().then(() => {
		store.set(isFullscreenAtom, true)
	})
}

export function exitFullscreen() {
	document.exitFullscreen().then(() => {
		store.set(isFullscreenAtom, false)
	})
}

export const projectActionToastAtom = atom<ToastContent | null>(null)

export interface ToastContent {
	date: Date //used for unique identifier
	content: string
}

export const showToast = (content: string) => {
	store.set(projectActionToastAtom, { date: new Date(), content })
}

export const expandedPanelsAtom = focusAtom(stateAtom, (optic) => optic.prop("expandedPanels"))

export const expandPanel = (key: string, expanded: boolean) => {
	store.set(expandedPanelsAtom, { ...store.get(expandedPanelsAtom), [key]: expanded })
}
